import { Platform, StyleSheet } from 'react-native';
import Markdown from 'react-native-markdown-display';
import { colors } from '../../theme/colors';
import { tokens } from '../../theme/tokens';

const fontFamily = Platform.select({
  ios: 'DMSans_400Regular',
  android: 'DMSans_400Regular',
  web: '"DM Sans", "Avenir Next", "SF Pro Display", "Helvetica Neue", sans-serif',
  default: 'DMSans_400Regular',
});

const codeFontFamily = Platform.select({
  ios: 'Menlo',
  android: 'monospace',
  web: '"Fira Code", Menlo, Monaco, monospace',
  default: 'monospace',
});

interface MarkdownTextProps {
  children: string;
  color?: string;
  fontSize?: number;
}

export function MarkdownText({
  children,
  color = colors.text.secondary,
  fontSize = tokens.fontSize.sm,
}: Readonly<MarkdownTextProps>) {
  const lineHeight = fontSize * 1.6;

  const codeBlockStyle = {
    backgroundColor: colors.background.tertiary,
    color: colors.primary[300],
    fontFamily: codeFontFamily,
    fontSize: fontSize * 0.9,
    lineHeight: fontSize * 0.9 * 1.5,
    padding: tokens.spacing.sm,
    borderRadius: tokens.radius.sm,
    marginVertical: tokens.spacing.xs,
  };

  const markdownStyles = StyleSheet.create({
    body: {
      color,
      fontSize,
      lineHeight,
      fontFamily,
    },
    paragraph: {
      marginTop: 0,
      marginBottom: fontSize * 0.5,
    },
    heading1: {
      color: colors.text.primary,
      fontSize: fontSize * 1.5,
      lineHeight: fontSize * 1.5 * 1.3,
      fontWeight: '700',
      marginTop: fontSize,
      marginBottom: fontSize * 0.5,
    },
    heading2: {
      color: colors.text.primary,
      fontSize: fontSize * 1.3,
      lineHeight: fontSize * 1.3 * 1.3,
      fontWeight: '700',
      marginTop: fontSize,
      marginBottom: fontSize * 0.4,
    },
    heading3: {
      color: colors.text.primary,
      fontSize: fontSize * 1.15,
      lineHeight: fontSize * 1.15 * 1.3,
      fontWeight: '600',
      marginTop: fontSize * 0.8,
      marginBottom: fontSize * 0.3,
    },
    strong: {
      color: colors.text.primary,
      fontWeight: '600',
    },
    em: {
      fontStyle: 'italic',
    },
    link: {
      color: colors.primary[400],
      textDecorationLine: 'underline',
    },
    blockquote: {
      backgroundColor: colors.background.tertiary,
      borderLeftWidth: 3,
      borderLeftColor: colors.primary[500],
      paddingLeft: tokens.spacing.sm,
      paddingVertical: tokens.spacing.xs,
      marginVertical: tokens.spacing.xs,
    },
    code_inline: {
      backgroundColor: colors.background.tertiary,
      color: colors.primary[400],
      fontFamily: codeFontFamily,
      fontSize: fontSize * 0.9,
      paddingHorizontal: 4,
      paddingVertical: 1,
      borderRadius: 4,
    },
    code_block: codeBlockStyle,
    fence: codeBlockStyle,
    bullet_list: {
      marginVertical: tokens.spacing.xs,
    },
    ordered_list: {
      marginVertical: tokens.spacing.xs,
    },
    list_item: {
      flexDirection: 'row',
      marginVertical: 2,
    },
    bullet_list_icon: {
      color,
      fontSize: fontSize * 0.8,
      marginRight: tokens.spacing.xs,
      lineHeight,
    },
    ordered_list_icon: {
      color,
      fontSize,
      marginRight: tokens.spacing.xs,
      lineHeight,
    },
    hr: {
      backgroundColor: colors.border.subtle,
      height: 1,
      marginVertical: tokens.spacing.sm,
    },
    table: {
      borderColor: colors.border.subtle,
      borderWidth: 1,
      borderRadius: tokens.radius.sm,
      marginVertical: tokens.spacing.xs,
    },
    thead: {
      backgroundColor: colors.background.tertiary,
    },
    th: {
      color: colors.text.primary,
      fontWeight: '600',
      padding: tokens.spacing.xs,
      borderColor: colors.border.subtle,
    },
    td: {
      color,
      padding: tokens.spacing.xs,
      borderColor: colors.border.subtle,
    },
    tr: {
      borderColor: colors.border.subtle,
    },
  });

  return <Markdown style={markdownStyles}>{children}</Markdown>;
}
